import { FormValidators, Obj, RxFormEvt, RxValidate } from '../types';

import { dropKey } from './general';

/**
 * the 'error' state is derived from
 *
 * 1. current error obj
 * 2. current event
 * 3. current form values
 * 4. submit count
 * 5. validators
 */
type Args = [RxFormEvt, Obj, number, FormValidators];

/**
 * @param outgoing
 * @param event
 * @param derivedValues
 * @param submitCount
 * @param validators
 */
export function errorUpdates(outgoing: Obj, [event, derivedValues, submitCount, validators]: Args): Obj {
    switch (event.type) {
        case 'field-unmount': {
            return dropKey(outgoing, event.field);
        }
        case 'submit': {
            const next = {} as {
                [index: string]: string | undefined;
            };
            Object.keys(validators).forEach((key) => {
                const curr = derivedValues[key];
                const validator = validators[key] && validators[key].fn;
                if (validator) {
                    const result = validator(curr, derivedValues);
                    const isValid = result === undefined;
                    if (!isValid) {
                        next[key] = result;
                    }
                }
            });
            return next;
        }
        case 'field-change': {
            if (submitCount === 0) return outgoing;
            const next = { ...outgoing };
            const key = event.field;
            delete next[key];

            const curr = derivedValues[key];
            const validator = validators[key];

            if (validator && validator.validateOnChange) {
                /**
                 * Get the validation answer from the field that actually changed first.
                 */
                const result = validator.fn(curr, derivedValues);
                const isValid = result === undefined;

                /**
                 * As a minimum, we set this fields error if it exists
                 */
                if (!isValid) {
                    next[key] = result;
                }

                /**
                 * If the `validateNotify` property exists, we'll through
                 * each item and run it's validation.
                 *
                 * Note: We currently only run the 'other' validations
                 * if the `current` validation (the field that changed) is
                 * now valid. This may need opening up to configuration later.
                 */
                if (isValid && Array.isArray(validator.validateNotify)) {
                    validator.validateNotify.forEach((key) => {
                        processNotifyField(key, next, derivedValues, validators);
                    });
                }
            }
            return next;
        }
        case 'field-blur': {
            const key = event.field;
            const curr = derivedValues[key];
            const validator = validators[key];
            const next = { ...outgoing };
            delete next[key];
            if (validator && validator.validateOnBlur) {
                const result = validator.fn(curr, derivedValues);
                const isValid = result === undefined;
                if (!isValid) {
                    next[key] = result;
                }
            }
            return next;
        }
        default:
            return outgoing;
    }
}

/**
 * @param key
 * @param next
 * @param values
 * @param validators
 */
function processNotifyField(key: string, next: Obj, values: Obj, validators: { [p: string]: RxValidate }): void {
    const curr = values[key];
    const validator = validators[key];
    if (validator && validator.validateOnChange) {
        const result = validator.fn(curr, values);
        const isValid = result === undefined;
        if (!isValid) {
            next[key] = result;
        } else {
            delete next[key];
        }
    }
}
